import React, { useEffect, useState } from "react";
import CrudPage from "@/components/common/CrudPage";
import { useApp } from "@/contexts/AppContext";
import { api, formatError } from "@/lib/api";
import { ListChecks, Gavel } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { k: "title", label: "Τίτλος εκκρεμότητας" },
  { k: "description", label: "Περιγραφή", type: "textarea", rows: 3 },
  { k: "assigned_to", label: "Ανατέθηκε σε" },
  { k: "due_date", label: "Προθεσμία", type: "date" },
  { k: "priority", label: "Προτεραιότητα", type: "select", options: [
    { value: "low", label: "Χαμηλή" }, { value: "medium", label: "Μέτρια" }, { value: "high", label: "Υψηλή" },
  ]},
  { k: "status", label: "Κατάσταση", type: "select", options: [
    { value: "todo", label: "Εκκρεμεί" }, { value: "in_progress", label: "Σε εξέλιξη" }, { value: "done", label: "Ολοκληρώθηκε" },
  ]},
  { k: "section", label: "Σχετική ενότητα" },
  { k: "notes", label: "Σημειώσεις", type: "textarea", rows: 2 },
];

function DeadlineGenerator({ onGenerated }) {
  const { currentCase } = useApp();
  const [rules, setRules] = useState([]);
  const [ruleKey, setRuleKey] = useState("");
  const [triggerDate, setTriggerDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  useEffect(() => {
    api.get("/deadlines/rules").then(r => {
      setRules(r.data || []);
      if (r.data?.length) setRuleKey(r.data[0].key);
    }).catch(() => {});
  }, []);

  const rule = rules.find(r => r.key === ruleKey);

  const generate = async () => {
    if (!ruleKey || !triggerDate || !currentCase) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${currentCase.id}/deadlines/generate`, {
        rule_key: ruleKey, trigger_date: triggerDate,
      });
      setLast(data.computation);
      toast.success(`Προθεσμία: ${new Date(data.computation.due_date).toLocaleDateString("el-GR")} — δημιουργήθηκε ως εκκρεμότητα`);
      onGenerated();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface-card p-5 mb-6" data-testid="deadline-generator">
      <h3 className="font-heading font-semibold text-[13.5px] mb-1 flex items-center gap-2">
        <Gavel className="size-4 gold-text"/>Γεννήτρια προθεσμιών ΚΠΔ
      </h3>
      <p className="text-[11.5px] text-muted-foreground mb-3">
        Ντετερμινιστικός υπολογισμός κατά ΚΠΔ 168 (εξαιρετέες ημέρες, αναστολή Αυγούστου).
        Κάθε προθεσμία σημαίνεται <b>ΠΡΟΣ ΕΠΑΛΗΘΕΥΣΗ</b> — επιβεβαιώστε πάντα με το ισχύον κείμενο του ΚΠΔ.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Ένδικο μέσο / ενέργεια</label>
          <select data-testid="deadline-rule-select" value={ruleKey} onChange={e => setRuleKey(e.target.value)}
            className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[12.5px]">
            {rules.map(r => <option key={r.key} value={r.key}>{r.title} ({r.days} ημ. — {r.legal_basis})</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">
            {rule?.trigger_label || "Ημερομηνία εκκίνησης"}
          </label>
          <input data-testid="deadline-trigger-date" type="date" value={triggerDate} onChange={e => setTriggerDate(e.target.value)}
            className="mt-1.5 bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[12.5px]"/>
        </div>
        <button data-testid="deadline-generate-btn" onClick={generate} disabled={busy || !triggerDate}
          className="px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[12.5px] disabled:opacity-60">
          {busy ? "…" : "Υπολογισμός"}
        </button>
      </div>
      {last && (
        <div className="mt-3 p-3 rounded border border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.06)] text-[12px]" data-testid="deadline-result">
          <div className="font-semibold gold-text">Λήξη: {new Date(last.due_date).toLocaleDateString("el-GR")} · {last.legal_basis} · ΠΡΟΣ ΕΠΑΛΗΘΕΥΣΗ</div>
          <ul className="mt-1 text-muted-foreground list-disc pl-4">
            {last.computation_steps.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
          {last.notes && <div className="mt-1 text-muted-foreground">⚠ {last.notes}</div>}
        </div>
      )}
    </div>
  );
}

export default function Tasks() {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div>
      <DeadlineGenerator onGenerated={() => setRefreshKey(k => k + 1)}/>
      <CrudPage
        key={refreshKey}
        slug="tasks" title="Εκκρεμότητες"
        subtitle="Διαχείριση εργασιών για την υπόθεση με προτεραιότητες και προθεσμίες."
        emptyIcon={ListChecks} fields={FIELDS}
        defaultItem={{ priority: "medium", status: "todo" }}
        listColumns={{
          title: it => it.title,
          subtitle: it => `${it.assigned_to || "—"} · ${it.due_date ? new Date(it.due_date).toLocaleDateString("el-GR") : "—"}`,
          body: it => it.description,
        }}
        badges={{
          priority: { field: "priority" },
          status: { field: "status" },
        }}
      />
    </div>
  );
}
